import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowsClockwiseIcon,
  PlayIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { useRexelDiscoveryJobs } from '@/api/rexel/useRexelDiscoveryJobs';
import { useRexelProcessingJobs } from '@/api/rexel/useRexelProcessingJobs';
import { useTriggerFileDiscovery } from '@/api/rexel/useRexelActions';
import { DiscoveryJobsQuery, ProcessingJobsQuery } from '@/api/rexel/types';
import ProcessingJobsTable from './components/ProcessingJobsTable';
import DiscoveryJobsTable from './components/DiscoveryJobsTable';
import JobStatsCards from './components/JobStatsCards';
import Page from 'src/components/page/Page';

const RexelMonitoringPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'processing' | 'discovery'>(
    'processing',
  );
  const [processingFilters, setProcessingFilters] =
    useState<ProcessingJobsQuery>({
      page: 1,
      limit: 25,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    });
  const [discoveryFilters, setDiscoveryFilters] = useState<DiscoveryJobsQuery>({
    page: 1,
    limit: 25,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);

  // API hooks
  const {
    data: processingJobsData,
    isLoading: loadingProcessing,
    refetch: refetchProcessing,
  } = useRexelProcessingJobs(processingFilters);
  const {
    data: discoveryJobsData,
    isLoading: loadingDiscovery,
    refetch: refetchDiscovery,
  } = useRexelDiscoveryJobs(discoveryFilters);
  const triggerDiscovery = useTriggerFileDiscovery();

  const processingJobs = processingJobsData?.data || [];
  const discoveryJobs = discoveryJobsData?.data || [];

  // Update last fetch time when data changes
  useEffect(() => {
    if (processingJobsData || discoveryJobsData) {
      setLastFetchTime(new Date());
    }
  }, [processingJobsData, discoveryJobsData]);

  const handleRefresh = () => {
    refetchProcessing();
    refetchDiscovery();
    setLastFetchTime(new Date());
  };

  const handleTriggerDiscovery = () => {
    triggerDiscovery.mutate({
      priority: 'normal',
    });
  };

  const handleProcessingFilterChange = (
    filters: Partial<ProcessingJobsQuery>,
  ) => {
    setProcessingFilters((prev) => ({ ...prev, ...filters, page: 1 }));
  };

  const handleDiscoveryFilterChange = (
    filters: Partial<DiscoveryJobsQuery>,
  ) => {
    setDiscoveryFilters((prev) => ({ ...prev, ...filters, page: 1 }));
  };

  return (
    <Page
      title="Rexel File Processing Monitor"
      subtitle="Monitor and manage Rexel E844SD file processing jobs"
      actions={
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 self-start">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loadingProcessing || loadingDiscovery}
            >
              <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleTriggerDiscovery}
              disabled={triggerDiscovery.isPending}
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              Trigger Discovery
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {(loadingProcessing || loadingDiscovery) && (
              <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {lastFetchTime && (
              <span className="text-xs text-muted-foreground">
                Last updated: {lastFetchTime.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      }
    >
      <div className="pt-6 space-y-6">
        {/* Job Statistics */}
        <JobStatsCards
          processingJobs={processingJobs}
          discoveryJobs={discoveryJobs}
        />

        {/* Tab Navigation */}
        <div className="border-b">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('processing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'processing'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Processing Jobs
              {(processingJobsData?.pagination?.total ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {processingJobsData?.pagination?.total ?? 0}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('discovery')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'discovery'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Discovery Jobs
              {(discoveryJobsData?.pagination?.total ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {discoveryJobsData?.pagination?.total ?? 0}
                </Badge>
              )}
            </button>
          </nav>
        </div>

        {/* Job Tables */}
        {activeTab === 'processing' && (
          <ProcessingJobsTable
            data={processingJobsData}
            isLoading={loadingProcessing}
            filters={processingFilters}
            onFiltersChange={handleProcessingFilterChange}
          />
        )}

        {activeTab === 'discovery' && (
          <DiscoveryJobsTable
            data={discoveryJobsData}
            isLoading={loadingDiscovery}
            filters={discoveryFilters}
            onFiltersChange={handleDiscoveryFilterChange}
          />
        )}
      </div>
    </Page>
  );
};

export default RexelMonitoringPage;
